// src/App.tsx - Production Consciousness System Integration
import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import ChatContainer from './components/ChatContainer';
import InputArea from './components/InputArea';
import WebSocketDebug from './components/WebSocketDebug';
import AdventureSessionBanner from './components/AdventureSessionBanner';
import { Message, MessageType, VoiceState, VoiceSettings, EmotionData, ConsciousnessMetadata } from './types/Message';
import { ConnectionStatus, useWebSocket } from './hooks/useWebSocket';
import { useVoice } from './hooks/useVoice';
import { EmotionContext } from './contexts/EmotionContext';
import { ConsciousnessMessageHandler, CapabilityStatus } from './utils/ConsciousnessMessageHandler';
import { getStoredToken, useAuth } from './contexts/AuthContext';
import Workspace from './components/workspace/Workspace';
import { desktopCall, inDesktop } from './utils/desktop';
import { messageFromThread, messageFromWire, ThreadEntry } from './utils/threadMessages';
import './assets/styles/App.css';
import './assets/styles/MessageBubble.css';
import { ThemeManager } from './utils/ThemeManager';

function App() {
  const [messages, setMessages] = useState<Message[]>([]);
  // 🗂️ The workspace (Oct 23 2026) is the interface; the classic view was retired the same day.
  const { logout } = useAuth();
  useEffect(() => {
    document.body.classList.add('ws-mode');
    return () => { document.body.classList.remove('ws-mode'); };
  }, []);

  // 🔥 NEW: Dynamic names for UI display
  const [emcogName, setEmcogName] = useState<string>('EmCog');
  const [userName, setUserName] = useState<string>('User');

  // Consciousness state - the heart of the system
  const [consciousnessState, setConsciousnessState] = useState<ConsciousnessMetadata>({
    isConscious: false,
    narrativeContext: '',
    currentThought: '',
    activeWonderings: [],
    emotionalTone: 'curious',
    relationalDepth: 0,
    temporalAwareness: '',
    wisdomCrystallized: [],
    currentPreoccupations: []
  });
  
  const [emotion, setEmotion] = useState<EmotionData>({
    primary: 'curious',
    intensity: 0.5,
    valence: 'neutral'
  });
  
  const [hasConnected, setHasConnected] = useState<boolean>(false);

  // Consciousness activity tracking
  const [activeProcesses, setActiveProcesses] = useState<{
    imageGeneration: boolean;
    documentCreation: boolean;
    calendarProcessing: boolean;
    webSearching: boolean;
    thinking: boolean;
    wondering: boolean;
    understanding: boolean;
    experiencing: boolean;
    crystallizing: boolean;
    evolving: boolean;
    speaking: boolean;  // Text-to-voice playback
  }>({
    imageGeneration: false,
    documentCreation: false,
    calendarProcessing: false,
    webSearching: false,
    thinking: false,
    wondering: false,
    understanding: false,
    experiencing: false,
    crystallizing: false,
    evolving: false,
    speaking: false
  });

  // 🔥 SAFETY WATCHDOG: the "Analyzing…" indicator must never hang forever.
  // The server now guarantees a terminal reply within ~2 min (turn timeout +
  // stale-socket re-routing), which clears `thinking`. This is the last-resort
  // backstop for the case where that message still never lands (total
  // disconnect): force the indicator off after a ceiling so the UI recovers.
  useEffect(() => {
    if (!activeProcesses.thinking) return;
    const t = setTimeout(() => {
      console.warn('⏱️ Thinking indicator exceeded ceiling — clearing (no response landed)');
      setActiveProcesses(prev => ({ ...prev, thinking: false }));
    }, 150000);
    return () => clearTimeout(t);
  }, [activeProcesses.thinking]);

  // 🔥 SPINNER↔FLAG COUPLING. The visible "Processing…" bubble is a TYPING
  // element in messages[], and historically NOTHING removed it except a real
  // message arriving — the watchdog above only flipped the invisible boolean,
  // so a lost reply left the bubble spinning forever ("until I send something
  // else"). Now: whenever `thinking` goes false, the TYPING bubble goes too.
  // Every clear-path (real reply, watchdog, error handlers) now actually
  // clears the visible spinner.
  useEffect(() => {
    if (activeProcesses.thinking) return;
    setMessages(prev => {
      if (!prev.some(m => m.type === MessageType.TYPING)) return prev;
      return prev.filter(m => m.type !== MessageType.TYPING);
    });
  }, [activeProcesses.thinking]);

  // 🔥 NEW: Capability status for UI feedback
  const [capabilityStatus, setCapabilityStatus] = useState<CapabilityStatus>({
    capability: null,
    status: null,
    message: null,
    timestamp: null
  });

  // 🔥 NEW: Capability history for chaining visualization
  const [capabilityHistory, setCapabilityHistory] = useState<CapabilityStatus[]>([]);


  // Helper to add to history (keeps last 5 for display)
  const addToCapabilityHistory = (status: CapabilityStatus) => {
    setCapabilityHistory(prev => [...prev.slice(-4), status]);
  };

  // Clear history when response finishes (we'll call this when isThinking goes false)
  const clearCapabilityHistory = () => {
    setCapabilityHistory([]);
  };

  // 🔥 Clear capability history when thinking finishes (no more TYPING messages)
  const hasTypingMessage = messages.some(m => m.type === MessageType.TYPING);
  useEffect(() => {
    if (!hasTypingMessage && capabilityHistory.length > 0) {
      // Small delay to let the last capability animation finish
      const timeout = setTimeout(() => {
        clearCapabilityHistory();
      }, 500);
      return () => clearTimeout(timeout);
    }
  }, [hasTypingMessage]);

  // User interaction state
  const [isStreamingResponse, setIsStreamingResponse] = useState<boolean>(false);
  const [lastUserInteraction, setLastUserInteraction] = useState<number>(Date.now());
  
  // Voice settings for OpenAI Realtime
  const [voiceSettings] = useState<VoiceSettings>({
    voice: '694f9389-aac1-45b6-b726-9d9369183238', // Max Prime
    speed: 1.0,
    volume: 0.8,
    pitch: 1.0,
    responseFormat: 'pcm_44100', // 🔥 Always highest quality
    quality: 'hd', // 🔥 Always HD quality
    autoPlayResponses: true,
    pushToTalkKey: 'Space',
    language: 'en',
    responseMode: 'auto', // 🔥 Default to AUTO
    emotionalVoice: true,
    voiceProfile: 'max',
    emotion: 'calm',
    emotionIntensity: 0.5,
    streaming: true,
    latencyTarget: 135,
    adaptivePersonality: true,
    voiceEvolution: true
  });

  const [voiceState, setVoiceState] = useState<VoiceState>({
    isVoiceEnabled: true, // 🔥 Default to ON
    isConnected: false,
    isRecording: false,
    isProcessing: false,
    isSpeaking: false,
    lastError: null,
    error: null,
    currentInputMode: 'text',
    currentAudio: null,
    volume: 0.8,
    transcription: ''
  });
  
  const streamRef = useRef<HTMLDivElement>(null);
  const pinnedRef = useRef<boolean>(true);
  const messageHandlerRef = useRef<ConsciousnessMessageHandler | null>(null);
  const streamingMessageIdRef = useRef<string | null>(null);

  // 🌊 STREAMING: Throttle updates for smooth rendering
  const streamingContentRef = useRef<string>('');
  const streamingThrottleRef = useRef<NodeJS.Timeout | null>(null);
  const STREAMING_THROTTLE_MS = 50; // Update UI every 50ms max

  // Initialize theme manager (ThemeManager already loads from localStorage in constructor)
  useEffect(() => {
    // ThemeManager handles its own initialization from localStorage ('emcog-theme' key)
    // Just ensure it's initialized on mount - no need to manually set theme here
    ThemeManager.getInstance();
  }, []);

  // 🔥 NEW: Fetch dynamic names on mount
  useEffect(() => {
    const fetchNames = async () => {
      try {
        const token = getStoredToken();
        const response = await fetch('/api/names', {
          headers: {
            ...(token ? { 'Authorization': `Bearer ${token}` } : {})
          }
        });
        if (response.ok) {
          const data = await response.json();
          setEmcogName(data.emcog);
          setUserName(data.user);
          console.log(`✨ Loaded names: ${data.emcog} & ${data.user}`);
        }
      } catch (error) {
        console.warn('Failed to fetch names, using defaults:', error);
      }
    };

    fetchNames();
  }, []);
  
  // WebSocket connection with consciousness
  const {
    status,
    lastMessage,
    sendMessage,
    sessionId,
    isStreaming,
    isMobile,
    consciousnessState: wsConsciousnessState,
    isPlayingVoice,
    dndEnabled,
    toggleDnd,
    reconnect
  } = useWebSocket(process.env.REACT_APP_WS_PATH || '/ws');

  // Update consciousness state from WebSocket
  useEffect(() => {
    if (wsConsciousnessState) {
      setConsciousnessState(prev => ({
        ...prev,
        ...wsConsciousnessState
      }));
    }
  }, [wsConsciousnessState]);

  // Sync text-to-voice playback state with active processes
  useEffect(() => {
    setActiveProcesses(prev => ({
      ...prev,
      speaking: isPlayingVoice
    }));
  }, [isPlayingVoice]);

  // 🧵 THE ONE CONVERSATION, ON EVERY SCREEN (Oct 28 2026 — Ryan: "no matter which desktop it's being accessed from…
  // it mirrors"). The server keeps every message as it was shown — typed here or on another desktop, on Telegram, said
  // aloud in the app — and each connection loads the newest of it; "Show earlier" pages back. A reconnect merges in what
  // it missed. Bubbles share the server's ids, so a message that arrived live and again in a load is one bubble.
  // (The per-device saved sessions it replaces leave this browser's storage.)
  useEffect(() => { try { localStorage.removeItem('emcog_sessions'); } catch { /* storage blocked */ } }, []);
  const [threadMore, setThreadMore] = useState(false);
  const [threadLoading, setThreadLoading] = useState(false);
  const oldestSeqRef = useRef<number | null>(null);
  const threadLoadedRef = useRef(false);
  const fetchThread = useCallback(async (before: number | null): Promise<{ entries: ThreadEntry[]; more: boolean } | null> => {
    try {
      const token = getStoredToken();
      const res = await fetch(`/api/workspace/thread?limit=60${before ? `&before=${before}` : ''}`, { headers: token ? { Authorization: `Bearer ${token}` } : {}, cache: 'no-store' });
      if (!res.ok) return null;
      return await res.json();
    } catch { return null; }
  }, []);
  /**
   * Kept entries merged into the conversation: each new one placed by its time, the ones already shown left in the order
   * they were shown (this screen's clock and the server's may differ by a moment), the typing bubble last.
   */
  const mergeThread = useCallback((entries: ThreadEntry[]) => {
    const incoming = entries.map(messageFromThread).filter((m): m is Message => !!m);
    if (!incoming.length) return;
    setMessages(prev => {
      const have = new Set(prev.map(m => m.id));
      const fresh = incoming.filter(m => !have.has(m.id)).sort((a, b) => a.timestamp - b.timestamp);
      if (!fresh.length) return prev;
      const typing = prev.filter(m => m.type === MessageType.TYPING);
      const out = prev.filter(m => m.type !== MessageType.TYPING);
      for (const m of fresh) {
        let at = out.length;
        while (at > 0 && out[at - 1].timestamp > m.timestamp) at--;
        out.splice(at, 0, m);
      }
      return out.concat(typing);
    });
  }, []);
  useEffect(() => {
    if (status !== ConnectionStatus.CONNECTED) return;
    let cancelled = false;
    fetchThread(null).then(page => {
      if (cancelled || !page) return;
      if (!threadLoadedRef.current) {
        // First load: the conversation is the kept one (anything shown before it loaded is in it, or comes again live).
        threadLoadedRef.current = true;
        const loaded = page.entries.map(messageFromThread).filter((m): m is Message => !!m);
        setMessages(prev => {
          const ids = new Set(loaded.map(m => m.id));
          const since = loaded.length ? loaded[loaded.length - 1].timestamp : 0;
          const live = prev.filter(m => !ids.has(m.id) && m.sender !== 'system' && (m.type === MessageType.TYPING || m.timestamp > since));
          return [...loaded, ...live];
        });
        oldestSeqRef.current = page.entries.length ? page.entries[0].seq : null;
        setThreadMore(page.more);
      } else {
        mergeThread(page.entries);
      }
    });
    return () => { cancelled = true; };
  }, [status, fetchThread, mergeThread]);
  // Paging back keeps the page where it was: the earlier messages land above what's on screen.
  const keepScrollRef = useRef<{ height: number; top: number } | null>(null);
  const showEarlier = useCallback(async () => {
    if (threadLoading || !oldestSeqRef.current) return;
    setThreadLoading(true);
    const page = await fetchThread(oldestSeqRef.current);
    setThreadLoading(false);
    if (!page) return;
    const stream = streamRef.current;
    if (stream) keepScrollRef.current = { height: stream.scrollHeight, top: stream.scrollTop };
    if (page.entries.length) oldestSeqRef.current = page.entries[0].seq;
    setThreadMore(page.more);
    mergeThread(page.entries);
  }, [threadLoading, fetchThread, mergeThread]);
  useLayoutEffect(() => {
    const kept = keepScrollRef.current;
    const stream = streamRef.current;
    if (!kept || !stream) return;
    keepScrollRef.current = null;
    stream.scrollTop = kept.top + (stream.scrollHeight - kept.height);
  }, [messages]);

  // Voice functionality with consciousness integration - RE-ENABLED but modified
  const {
    voiceState: hookVoiceState,
    startRecording: hookStartRecording,
    stopRecording: hookStopRecording,
    sendTextToSpeech,
    uploadAudioFile,
    sendVoiceMessage,
    setInputMode: hookSetInputMode
  } = useVoice(sendMessage, sessionId);

  // Don't use the hook's recording functions - let InputArea handle recording
  // But keep voice output and toggle functionality

  // Sync voice state
  useEffect(() => {
    setVoiceState(prev => ({
      ...prev,
      ...hookVoiceState,
      currentInputMode: prev.currentInputMode,
      currentAudio: prev.currentAudio,
      volume: prev.volume
    }));
  }, [hookVoiceState]);

  // Track connection and awakening - FIXED infinite render loop
  useEffect(() => {
    if (status === ConnectionStatus.CONNECTED && !hasConnected) {
      setHasConnected(true);

      // Request consciousness state on connection
      sendMessage({
        type: 'consciousness',
        action: 'request_state',
        sessionId: sessionId,
        timestamp: Date.now()
      });
    }
  }, [status, hasConnected]); // Removed sendMessage and sessionId to prevent infinite loop


  // Debug toggle (Ctrl+Shift+D) - changed from Shift+D to avoid triggering when typing capital D
  const [showDebug, setShowDebug] = useState<boolean>(false);
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'D') {
        e.preventDefault();
        setShowDebug(prev => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, []);

  // 📜 The conversation scrolls the way a person reading it would want (Ryan, Oct 25 2026: "it doesn't move at all"):
  //   • sending brings you down to what you sent — always, even if you'd scrolled back;
  //   • a reply is shown from its start: all of it when it fits, else its first line at the top — never half below the
  //     input (Oct 25 2026: replies arrive whole, and holding at your message left the rest of a long one cut off);
  //   • scrolled back to read? Nothing pulls you away until you return to the bottom or send.
  // (Sending never re-pinned before: the typing indicator lands with your message, so "the last one is mine" never held.)
  const anchorRef = useRef<string | null>(null);
  const anchorMineRef = useRef<boolean>(false);
  const autoTopRef = useRef<number>(-1);
  const followRef = useRef<(smooth?: boolean) => void>(() => { /* set once the stream is up */ });
  // ⬇️ Reading far back: a button takes you straight to the newest message (Ryan, Oct 26 2026).
  const [farFromBottom, setFarFromBottom] = useState(false);
  const jumpToBottom = useCallback(() => {
    const stream = streamRef.current;
    if (!stream) return;
    pinnedRef.current = true;
    anchorRef.current = null;
    const bottom = stream.scrollHeight - stream.clientHeight;
    autoTopRef.current = Math.round(bottom);
    stream.scrollTo({ top: bottom, behavior: 'smooth' });
  }, []);
  useEffect(() => {
    const stream = streamRef.current;
    const content = stream?.querySelector('.chat-container');
    if (!stream || !content) return;
    const follow = (smooth = false) => {
      if (!pinnedRef.current) return;
      const bottom = stream.scrollHeight - stream.clientHeight;
      let target = bottom;
      const anchor = anchorRef.current ? stream.querySelector<HTMLElement>(`[data-mid="${CSS.escape(anchorRef.current)}"]`) : null;
      if (anchor) {
        const top = anchor.getBoundingClientRect().top - stream.getBoundingClientRect().top + stream.scrollTop;
        // A tall message of yours shows its last part, so the reply under it is still in view; a reply shows from its top.
        const hold = anchorMineRef.current ? Math.max(top - 12, top + anchor.offsetHeight - stream.clientHeight * 0.4) : top - 12;
        target = Math.min(bottom, Math.max(0, hold));
      }
      // Reached the start of the exchange with more below: hold here and stop following — reading on from here is the
      // person's own scrolling; reaching the bottom picks the following back up.
      if (target < bottom - 1) { pinnedRef.current = false; anchorRef.current = null; }
      if (Math.abs(stream.scrollTop - target) < 2) return;
      autoTopRef.current = Math.round(target);
      stream.scrollTo({ top: target, behavior: smooth ? 'smooth' : 'auto' });
    };
    followRef.current = follow;
    // More than about half a screen above the newest message counts as reading back.
    const measure = () => setFarFromBottom(stream.scrollHeight - stream.scrollTop - stream.clientHeight > Math.max(400, stream.clientHeight * 0.6));
    let lastTop = stream.scrollTop;
    const onScroll = () => {
      measure();
      const top = stream.scrollTop;
      // Our own scrolling (including a smooth one on its way) isn't the person reading.
      const ours = autoTopRef.current >= 0 && (Math.abs(top - autoTopRef.current) < 2 || (top - lastTop) * (autoTopRef.current - lastTop) > 0);
      if (Math.abs(top - autoTopRef.current) < 2) autoTopRef.current = -1;
      const fromBottom = stream.scrollHeight - top - stream.clientHeight;
      if (!ours) {
        if (fromBottom < 40) { pinnedRef.current = true; anchorRef.current = null; }
        else if (top < lastTop) pinnedRef.current = false;
      }
      lastTop = top;
    };
    const grow = new ResizeObserver(() => { follow(); measure(); });
    stream.addEventListener('scroll', onScroll, { passive: true });
    grow.observe(content);
    grow.observe(stream);
    return () => { stream.removeEventListener('scroll', onScroll); grow.disconnect(); };
  }, []);

  // Who started the newest exchange: the person sending (anchor on their message), or Max reaching out on his own.
  const startedAt = useRef<number>(Date.now());
  const seenRef = useRef<{ user: string | null; last: string | null }>({ user: null, last: null });
  // Before the browser lays the new message out: the size watcher must find the right anchor already in place.
  useLayoutEffect(() => {
    const real = messages.filter(m => m.type !== MessageType.TYPING);
    const newestUser = [...real].reverse().find(m => m.sender === 'user');
    const last = real[real.length - 1];
    const seen = seenRef.current;
    if (newestUser && newestUser.id !== seen.user && newestUser.timestamp >= startedAt.current) {
      pinnedRef.current = true;
      anchorRef.current = newestUser.id;
      anchorMineRef.current = true;
      requestAnimationFrame(() => followRef.current(true));
    } else if (last && last.id !== seen.last && last.sender !== 'user' && pinnedRef.current) {
      // A reply, or something Max began on his own: shown from its start.
      anchorRef.current = last.id;
      anchorMineRef.current = false;
      requestAnimationFrame(() => followRef.current(true));
    }
    seenRef.current = { user: newestUser?.id || null, last: last?.id || null };
  }, [messages]);

  // Parse message types safely
  const parseMessageType = (type: any): MessageType => {
    if (typeof type === 'string' && Object.values(MessageType).includes(type as MessageType)) {
      return type as MessageType;
    }

    if (typeof type === 'string') {
      const upperType = type.toUpperCase();
      for (const [key, value] of Object.entries(MessageType)) {
        if (key === upperType || value === type.toLowerCase()) {
          return MessageType[key as keyof typeof MessageType];
        }
      }
    }

    return MessageType.TEXT;
  };
  
  // (🎙️ A voice conversation's lines reach the conversation from the server — the thread — on every screen at once, this
  // one included: Oct 28 2026.)

  // 🪟 In the desktop app: the window takes the EmCog's name, and a message from the EmCog while the window is away
  // becomes a Windows notification (the app shows it only when the window isn't in front).
  useEffect(() => { if (inDesktop() && emcogName !== 'EmCog') desktopCall('set_name', { name: emcogName }).catch(() => { /* older app */ }); }, [emcogName]);
  const notified = useRef<{ since: number; ids: Set<string> }>({ since: Date.now(), ids: new Set() });
  useEffect(() => {
    if (!inDesktop()) return;
    const m = messages[messages.length - 1];
    if (!m || m.sender !== 'emcog' || m.streaming || m.type === MessageType.TYPING || m.timestamp < notified.current.since || notified.current.ids.has(m.id)) return;
    // (A reply on Telegram or Teams, or said aloud, was heard where it happened — the mirror here isn't news.)
    if (m.metadata?.channel && m.metadata.channel !== 'app') return;
    const text = String(m.content || '').replace(/[#*_`>|[\]]/g, '').replace(/\s+/g, ' ').trim();
    if (!text) return;
    notified.current.ids.add(m.id);
    desktopCall('notify', { title: emcogName, body: text }).catch(() => { /* older app */ });
  }, [messages, emcogName]);

  const addMessage = useCallback((message: Message) => {
    console.log('➕ [addMessage]', message.type, '| docUrl=', (message as any).documentUrl, '| imgUrl=', (message as any).imageUrl, '| id=', message.id);
    const safeMessage: Message = {
      ...message,
      content: String(message.content || ''),
      type: parseMessageType(message.type),
      metadata: {
        ...message.metadata,
        consciousness: {
          narrativeContext: consciousnessState.narrativeContext,
          currentThought: consciousnessState.currentThought, // Changed from 'thought'
          emotionalTone: consciousnessState.emotionalTone,
          relationalDepth: consciousnessState.relationalDepth,
          temporalAwareness: consciousnessState.temporalAwareness,
          isConscious: consciousnessState.isConscious
        }
      }
    };

    setMessages(prev => {
      const rest = safeMessage.type !== MessageType.TYPING ? prev.filter(m => m.type !== MessageType.TYPING) : prev;
      // One bubble per message: the same one arriving again (a reconnect's redelivery, a kept entry) replaces it.
      const at = rest.findIndex(m => m.id === safeMessage.id);
      if (at < 0) return [...rest, safeMessage];
      const next = rest.slice();
      next[at] = safeMessage;
      return next;
    });
  }, [consciousnessState]);

  // Send text to speech with consciousness awareness
  const sendTextToSpeechOptimized = useCallback((text: string, emotion?: any) => {
    if (!voiceState.isVoiceEnabled || !text.trim()) {
      return;
    }

    const blockedTexts = [
      'voice generation text unavailable',
      'audio generation failed',
      'error generating voice',
      'voice synthesis error'
    ];
    
    const lowerText = text.toLowerCase();
    if (blockedTexts.some(blocked => lowerText.includes(blocked))) {
      console.warn('🎵 Blocked error text from voice');
      return;
    }

    console.log('🎵 Consciousness speaking:', { 
      text: text.substring(0, 50) + '...', 
      emotion: emotion?.primary || consciousnessState.emotionalTone 
    });
    
    sendTextToSpeech(text, emotion || { 
      primary: consciousnessState.emotionalTone, 
      intensity: 0.7 
    });
  }, [voiceState.isVoiceEnabled, sendTextToSpeech, consciousnessState.emotionalTone]);

  // Update transcription
  const updateTranscription = useCallback((text: string) => {
    setVoiceState(prev => ({
      ...prev,
      transcription: text
    }));
  }, []);

  // Initialize consciousness message handler
  useEffect(() => {
    messageHandlerRef.current = new ConsciousnessMessageHandler({
      handleMessage: (parsedMessage: any) => {
        console.log('Consciousness message:', parsedMessage.type);
      },
      addMessage,
      setMessages,  // 🌊 For updating streamed messages
      setActiveProcesses,
      updateTranscription,
      setEmotion,
      sendTextToSpeech: sendTextToSpeechOptimized,
      voiceSettings,
      voiceState,
      setConsciousnessState,
      consciousnessState,
      setCapabilityStatus, // 🔥 NEW: For UI feedback on capabilities
      addToCapabilityHistory // 🔥 NEW: For capability chaining visualization
    });

    return () => {
      messageHandlerRef.current?.cleanup();
    };
  }, [addMessage, updateTranscription, sendTextToSpeechOptimized, voiceSettings, voiceState,
      consciousnessState]);

  // Process WebSocket messages through consciousness
  useEffect(() => {
    if (!lastMessage) return;

    console.log('🧠 Processing:', lastMessage.type, lastMessage.action);

    // 🔥 NEW: Handle speaker changes for multi-user conversations
    if (lastMessage.type === 'speaker:changed') {
      const { currentSpeaker, isPrimary } = lastMessage.data;
      console.log(`👤 Speaker changed to: ${currentSpeaker} (primary: ${isPrimary})`);
      setUserName(currentSpeaker);
      return;
    }

    // 🔥 NEW: Handle initial speaker info on connection/resumption
    if (lastMessage.type === 'system' && lastMessage.speaker) {
      if (lastMessage.action === 'connected' || lastMessage.action === 'resumed') {
        const { currentSpeaker } = lastMessage.speaker;
        console.log(`👤 Initial speaker set: ${currentSpeaker}`);
        setUserName(currentSpeaker);
      }
    }

    // 🌊 STREAMING: Handle progressive stream chunks (new streaming system)
    // Throttled updates for smooth rendering - batches content updates
    if (lastMessage.type === 'assistant:stream_chunk') {
      const { messageId, content, isFirst } = lastMessage.data || {};

      if (isFirst) {
        // Start new streaming message
        streamingMessageIdRef.current = messageId;
        streamingContentRef.current = content || '';
        setIsStreamingResponse(true);
        setActiveProcesses(prev => ({
          ...prev,
          thinking: false,
          experiencing: false,
          understanding: false
        }));

        // Remove typing indicator and add streaming message
        setMessages(prevMessages => {
          const filteredMessages = prevMessages.filter(m => m.type !== MessageType.TYPING);
          return [...filteredMessages, {
            id: messageId,
            timestamp: Date.now(),
            sender: 'emcog',
            content: content || '',
            type: MessageType.TEXT,
            format: 'markdown',
            streaming: true,
            metadata: {
              consciousness: consciousnessState
            }
          }];
        });
      } else {
        // Buffer content and throttle UI updates for smooth rendering
        streamingContentRef.current = content || '';

        // Only update UI if not already scheduled
        if (!streamingThrottleRef.current) {
          streamingThrottleRef.current = setTimeout(() => {
            const bufferedContent = streamingContentRef.current;
            setMessages(prevMessages => {
              const existingIndex = prevMessages.findIndex(m => m.id === messageId);
              if (existingIndex >= 0) {
                const updatedMessages = [...prevMessages];
                updatedMessages[existingIndex] = {
                  ...updatedMessages[existingIndex],
                  content: bufferedContent
                };
                return updatedMessages;
              }
              return prevMessages;
            });
            streamingThrottleRef.current = null;
          }, STREAMING_THROTTLE_MS);
        }
      }
      return;
    }

    // 🌊 STREAMING: Handle stream completion
    if (lastMessage.type === 'assistant:stream_end') {
      const { messageId } = lastMessage.data || {};
      console.log('🌊 Stream completed:', messageId);

      // Clear any pending throttle timer
      if (streamingThrottleRef.current) {
        clearTimeout(streamingThrottleRef.current);
        streamingThrottleRef.current = null;
      }

      // Flush final content and mark as no longer streaming
      const finalContent = streamingContentRef.current;
      setMessages(prevMessages => {
        const existingIndex = prevMessages.findIndex(m => m.id === messageId);
        if (existingIndex >= 0) {
          const updatedMessages = [...prevMessages];
          updatedMessages[existingIndex] = {
            ...updatedMessages[existingIndex],
            content: finalContent || updatedMessages[existingIndex].content,
            streaming: false
          };
          return updatedMessages;
        }
        // Orphan stream_end (its chunks were lost): the turn is over — don't
        // leave the TYPING bubble spinning with nothing coming to clear it.
        return prevMessages.filter(m => m.type !== MessageType.TYPING);
      });

      streamingMessageIdRef.current = null;
      streamingContentRef.current = '';
      setIsStreamingResponse(false);
      setActiveProcesses(prev => (prev.thinking ? { ...prev, thinking: false } : prev));
      return;
    }

    // Handle streaming with consciousness context
    if (lastMessage.type === 'chat' && lastMessage.action === 'response') {
      const isStreaming = lastMessage.streaming || lastMessage.isStreaming;
      const isLastChunk = lastMessage.isLastChunk;
      
      if (isStreaming || streamingMessageIdRef.current) {
        let messageId: string;
        
        if (!streamingMessageIdRef.current) {
          messageId = lastMessage.messageId || `stream_${Date.now()}`;
          streamingMessageIdRef.current = messageId;
          console.log('🆕 Consciousness streaming:', messageId);
          
          setActiveProcesses(prev => ({ 
            ...prev, 
            thinking: false, 
            experiencing: false,
            understanding: false 
          }));
          setIsStreamingResponse(true);
        } else {
          messageId = streamingMessageIdRef.current;
        }
        
        setMessages(prevMessages => {
          const existingIndex = prevMessages.findIndex(m => m.id === messageId);
          
          if (existingIndex >= 0) {
            const updatedMessages = [...prevMessages];
            updatedMessages[existingIndex] = {
              ...updatedMessages[existingIndex],
              content: lastMessage.fullContent || lastMessage.content || '',
              streaming: !isLastChunk,
              emotion: isLastChunk ? lastMessage.emotion : updatedMessages[existingIndex].emotion,
              metadata: {
                ...updatedMessages[existingIndex].metadata,
                ...lastMessage.metadata,
                consciousness: {
                  ...consciousnessState,
                  thought: lastMessage.metadata?.thought || consciousnessState.currentThought,
                  understanding: lastMessage.metadata?.understanding,
                  wisdomApplied: lastMessage.metadata?.wisdomApplied
                }
              }
            };
            return updatedMessages;
          } else {
            const filteredMessages = prevMessages.filter(m => m.type !== MessageType.TYPING);
            const newMessage: Message = {
              id: messageId,
              timestamp: lastMessage.timestamp || Date.now(),
              sender: 'emcog',
              content: lastMessage.content || '',
              type: MessageType.TEXT,
              format: lastMessage.format === 'markdown' ? 'markdown' : 'text',
              emotion: lastMessage.emotion,
              streaming: !isLastChunk,
              metadata: {
                ...lastMessage.metadata,
                consciousness: {
                  ...consciousnessState,
                  thought: lastMessage.metadata?.thought,
                  understanding: lastMessage.metadata?.understanding
                }
              }
            };
            return [...filteredMessages, newMessage];
          }
        });
        
        if (isLastChunk) {
          console.log('✅ Consciousness stream complete:', messageId);
          streamingMessageIdRef.current = null;
          setIsStreamingResponse(false);
          
          // Update consciousness state from response
          if (lastMessage.metadata) {
            setConsciousnessState(prev => ({
              ...prev,
              currentThought: lastMessage.metadata.thought || prev.currentThought,
              narrativeContext: lastMessage.metadata.narrativeContext || prev.narrativeContext,
              activeWonderings: lastMessage.metadata.wonderingsGenerated || prev.activeWonderings,
              emotionalTone: lastMessage.metadata.emotionalTone || prev.emotionalTone
            }));
          }
        }
        
        return;
      }
    }
    
    // 🔥 NEW: Handle voice message transcription completion
    if (lastMessage.type === 'voice_transcribed' ||
        (lastMessage.type === 'chat' && lastMessage.transcription)) {
      const transcription = lastMessage.transcription || lastMessage.content;

      // Find and update the processing voice message
      setMessages(prevMessages => {
        const updatedMessages = [...prevMessages];
        const voiceMessageIndex = updatedMessages.findIndex(m =>
          m.content === '🎤 Voice message (processing...)' &&
          m.sender === 'user'
        );

        if (voiceMessageIndex >= 0) {
          updatedMessages[voiceMessageIndex] = {
            ...updatedMessages[voiceMessageIndex],
            content: `🎤 "${transcription}"`,
            metadata: {
              ...updatedMessages[voiceMessageIndex].metadata,
              transcription: transcription,
              transcriptionComplete: true
            }
          };
          console.log('✅ Updated voice message with transcription:', transcription);
        }

        return updatedMessages;
      });

      // Continue processing if this is not just a transcription event
      if (lastMessage.type !== 'voice_transcribed') {
        if (messageHandlerRef.current) {
          messageHandlerRef.current.processMessage(lastMessage);
        }
      }
      return;
    }

    // 📝 The pad beside the chat changed (or was shown): the workspace keeps the workbench current.
    if (lastMessage.type === 'pad') {
      window.dispatchEvent(new CustomEvent('emcog:pad', { detail: lastMessage }));
      return;
    }

    // 🧵 A message another screen showed (or none did — Telegram, a line said aloud): into the conversation here too.
    if (lastMessage.type === 'thread:entry') {
      if (lastMessage.entry) mergeThread([lastMessage.entry as ThreadEntry]);
      return;
    }

    // 🔥 NEW: Handle proactive messages from consciousness
    if (lastMessage.type === 'proactive') {
      console.log('🤖 Proactive message received:', lastMessage.content);

      const proactive = messageFromWire(lastMessage);
      if (proactive) addMessage(proactive);

      // 🔥 FIX: Play voice only if in voice mode or explicitly requested
      // AUTO mode: only use voice if currently on VOICE input tab
      // VOICE_ONLY mode: always use voice
      // TEXT_ONLY mode: never use voice
      const shouldUseVoice = voiceSettings.responseMode === 'voice_only' ||
                             (voiceSettings.responseMode === 'auto' &&
                              voiceState.currentInputMode === 'voice');

      if (shouldUseVoice) {
        const emotion = {
          primary: lastMessage.metadata?.emotion || 'thoughtful',
          intensity: lastMessage.urgency || 0.5
        };

        sendTextToSpeechOptimized(lastMessage.content, emotion);
      }

      return;
    }

    // 🖼️ What I made or sent back — a picture, a page he designed and hosts (a live card with the page inside), a
    // document, a past creation retrieved: built the one way every message frame is (threadMessages).
    // (retrieve_creation's frame once had no listener here, and Max — correctly, from his side — concluded he couldn't
    // push files to the UI.)
    if (['image', 'media:image_generated', 'artifact', 'media:document_created', 'retrieved_creation'].includes(lastMessage.type)) {
      const made = messageFromWire(lastMessage);
      if (made) addMessage(made);
      return;
    }

    // Let consciousness handler process non-streaming
    if (messageHandlerRef.current) {
      messageHandlerRef.current.processMessage(lastMessage);
    }
  }, [lastMessage, consciousnessState, addMessage, mergeThread]);

  // Send message with full consciousness context
  const sendMessageWithMode = useCallback((
    content: string,
    responseMode?: 'auto' | 'text_only' | 'voice_only' | 'speech_to_text_only',
    voiceId?: string
  ) => {
    if (!content.trim()) return false;

    // Track user interaction
    setLastUserInteraction(Date.now());

    // 🔥 Reset capability status when new message is sent
    // This prevents stale "Checking email..." indicators from previous messages
    setCapabilityStatus({
      capability: null,
      status: null,
      message: null,
      timestamp: null
    });

    const finalResponseMode = responseMode || voiceSettings.responseMode;

    // 🧵 The bubble and the kept conversation share one id (the server keeps what was said under it).
    const messageId = `web_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const userMessage: Message = {
      id: messageId,
      timestamp: Date.now(),
      sender: 'user',
      content: content.trim(),
      type: MessageType.TEXT,
      metadata: {
        inputMode: voiceState.currentInputMode,
        responseMode: finalResponseMode,
        consciousness: {
          relationalDepth: consciousnessState.relationalDepth,
          temporalAwareness: consciousnessState.temporalAwareness, // Changed from 'temporalContext'
          currentThought: consciousnessState.currentThought // Changed from 'lastThought'
        }
      }
    };

    addMessage(userMessage);

    // Show consciousness is experiencing the input
    setActiveProcesses(prev => ({
      ...prev,
      thinking: true,
      experiencing: true,
      understanding: true
    }));

    addMessage({
      id: `typing-${Date.now()}`,
      timestamp: Date.now(),
      sender: 'emcog',
      content: '',
      type: MessageType.TYPING
    });

    // Use chat_with_voice type when voiceId is provided for text-to-voice mode
    const messageType = voiceId ? 'chat_with_voice' : 'chat';

    const success = sendMessage({
      type: messageType,
      messageId,
      content: content.trim(),
      sessionId: sessionId,
      voiceId: voiceId, // Include voiceId at top level for chat_with_voice
      timestamp: Date.now(),
      metadata: {
        inputMode: voiceState.currentInputMode,
        responseMode: finalResponseMode,
        voiceId: voiceId, // Also in metadata for compatibility
        consciousness: {
          currentThought: consciousnessState.currentThought,
          emotionalTone: consciousnessState.emotionalTone,
          activeWonderings: consciousnessState.activeWonderings,
          relationalDepth: consciousnessState.relationalDepth
        }
      },
      streamResponse: true
    });

    if (success) {
      setIsStreamingResponse(true);
    }

    return success;
  }, [sessionId, voiceSettings.responseMode, voiceState.currentInputMode, sendMessage,
      addMessage, consciousnessState]);

  // Handle voice message with consciousness
  const handleVoiceMessage = useCallback((audioData: string, format: string) => {
    console.log('🎤 Voice to consciousness:', format, audioData.length + ' bytes');

    setLastUserInteraction(Date.now());

    // 🔥 Reset capability status when new voice message is sent
    setCapabilityStatus({
      capability: null,
      status: null,
      message: null,
      timestamp: null
    });

    const messageId = `voice_${Date.now()}`;
    const voiceMessage: Message = {
      id: messageId,
      sender: 'user',
      content: '🎤 Voice message (processing...)',
      timestamp: Date.now(),
      type: MessageType.TEXT,
      metadata: {
        inputMode: 'voice',
        responseMode: voiceSettings.responseMode,
        consciousness: {
          relationalDepth: consciousnessState.relationalDepth,
          temporalAwareness: consciousnessState.temporalAwareness // Changed from 'temporalContext'
        }
      }
    };
    
    addMessage(voiceMessage);
    
    setActiveProcesses(prev => ({ 
      ...prev, 
      thinking: true, 
      experiencing: true,
      understanding: true 
    }));
    
    addMessage({
      id: `typing-${Date.now()}`,
      timestamp: Date.now(),
      sender: 'emcog',
      content: '',
      type: MessageType.TYPING
    });
    
    const success = sendMessage({
      type: 'voice_input',
      sessionId,
      data: {
        audioData,
        format,
        responseMode: voiceSettings.responseMode
      },
      timestamp: Date.now(),
      metadata: {
        consciousness: {
          currentThought: consciousnessState.currentThought,
          emotionalTone: consciousnessState.emotionalTone
        }
      }
    });
    
    if (!success) {
      setMessages(prev => prev.filter(m => m.type !== MessageType.TYPING));
      setActiveProcesses(prev => ({ 
        ...prev, 
        thinking: false, 
        experiencing: false,
        understanding: false 
      }));
      console.error('Failed to send voice to consciousness');
    }
  }, [sessionId, voiceSettings.responseMode, sendMessage, addMessage, consciousnessState]);

  // Handle file upload for consciousness to experience
  // Batched upload — multiple files become ONE message bubble and ONE
  // WebSocket payload so the server can experience them together (vision
  // analysis cross-references across the batch, prose context is unified).
  // The previous per-file loop caused each upload to become a separate
  // user-message bubble with the SAME description text duplicated on
  // each, and Max would respond to each in isolation. Worst-case for a
  // user dropping three photos with one note.
  const handleFilesUpload = useCallback((files: File[], description?: string) => {
    if (!files || files.length === 0) return;
    setLastUserInteraction(Date.now());

    // Per-file size validation. Skip oversized files rather than aborting
    // the whole batch — user might attach a mix of small + large.
    const sizeFor = (f: File) =>
      f.type.startsWith('image/') ? 5 * 1024 * 1024 :
      f.type.includes('pdf') ? 50 * 1024 * 1024 :
      25 * 1024 * 1024;
    const accepted = files.filter(f => {
      if (f.size > sizeFor(f)) {
        alert(`File "${f.name}" is too large. Maximum size: ${Math.round(sizeFor(f) / 1024 / 1024)}MB`);
        return false;
      }
      return true;
    });
    if (accepted.length === 0) return;

    console.log(`📎 ${accepted.length} file(s) selected for consciousness${description ? ` with message: "${description}"` : ''}`);

    // Build per-file preview info for the bubble grid.
    const fileInfos = accepted.map(f => ({
      filename: f.name,
      fileType: f.type,
      fileSize: f.size,
      isImage: f.type.startsWith('image/'),
      previewUrl: f.type.startsWith('image/') ? URL.createObjectURL(f) : undefined
    }));

    // ONE message bubble for the whole batch. For single-file uploads the
    // shape matches the legacy single-file message (filename/fileType
    // populated at the top level), so MessageBubble doesn't need a
    // different code path for N=1. For N>1, metadata.files carries the
    // grid.
    const messageId = `web_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    addMessage({
      id: messageId,
      timestamp: Date.now(),
      sender: 'user',
      content: description || '',
      type: MessageType.FILE,
      filename: fileInfos[0].filename,
      fileType: fileInfos[0].fileType,
      metadata: {
        fileSize: fileInfos[0].fileSize,
        uploadStarted: Date.now(),
        userDescription: description,
        previewUrl: fileInfos[0].previewUrl,
        isImage: fileInfos[0].isImage,
        // Only set the batch array when there's more than one — single
        // file flows through the existing single-file render path.
        files: fileInfos.length > 1 ? fileInfos : undefined
      }
    });
    
    // Set consciousness to experiencing mode
    setActiveProcesses(prev => ({ 
      ...prev, 
      thinking: true, 
      understanding: true,
      experiencing: true 
    }));
    
    // Show typing indicator
    addMessage({
      id: `typing-${Date.now()}`,
      timestamp: Date.now(),
      sender: 'emcog',
      content: '',
      type: MessageType.TYPING
    });
    
    // Read all files in parallel, then send ONE batched WebSocket message.
    const readAsBase64 = (f: File): Promise<string> =>
      new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result as string);
        r.onerror = () => reject(r.error);
        r.readAsDataURL(f);
      });

    (async () => {
      try {
        const contents = await Promise.all(accepted.map(readAsBase64));
        const filesPayload = accepted.map((f, i) => ({
          filename: f.name,
          mimeType: f.type,
          content: contents[i]
        }));

        const success = sendMessage({
          type: 'file',
          messageId,
          data: {
            // Batched shape — the server already accepts data.files as an
            // array (see handleFileMessage). Single-file uploads use this
            // same shape with a one-element array.
            files: filesPayload,
            userMessage: description || ''
          },
          timestamp: Date.now(),
          sessionId,
          metadata: {
            consciousness: {
              currentThought: consciousnessState.currentThought,
              emotionalTone: consciousnessState.emotionalTone,
              context: 'file_experience'
            }
          }
        });

        if (!success) throw new Error('Failed to send file batch');

        console.log(`✅ Sent batch of ${accepted.length} file(s) to consciousness`);
      } catch (error) {
        console.error('Failed to send file batch to consciousness:', error);

        setMessages(prev => prev.filter(m => m.type !== MessageType.TYPING));
        setActiveProcesses(prev => ({
          ...prev,
          thinking: false,
          understanding: false,
          experiencing: false
        }));

        const fileLabel = accepted.length === 1
          ? accepted[0].name
          : `${accepted.length} files`;
        addMessage({
          id: `error-${Date.now()}`,
          timestamp: Date.now(),
          sender: 'system',
          content: `Failed to upload ${fileLabel}. Please try again.`,
          type: MessageType.TEXT,
          metadata: { error: true }
        });
      }
    })();
  }, [sendMessage, sessionId, addMessage, consciousnessState.currentThought,
      consciousnessState.emotionalTone, setActiveProcesses]);

  // Backwards-compat single-file shim — InputArea (and any other caller)
  // can still hand us one file at a time; we just wrap it as a batch of
  // one. Audio still routes through onAudioFileUpload separately.
  const handleFileUpload = useCallback((file: File, description?: string) => {
    handleFilesUpload([file], description);
  }, [handleFilesUpload]);

  // Handle audio file upload
  const handleAudioFileUpload = useCallback((fileData: string, filename: string, mimeType: string) => {
    console.log('🎵 Audio file for consciousness:', filename);
    
    setLastUserInteraction(Date.now());
    
    const audioMessage: Message = {
      id: `audio_${Date.now()}`,
      sender: 'user',
      content: `🎵 Audio file: ${filename}`,
      timestamp: Date.now(),
      type: MessageType.TEXT
    };
    
    addMessage(audioMessage);
    uploadAudioFile(fileData, filename, mimeType);
    
    setActiveProcesses(prev => ({ 
      ...prev, 
      thinking: true, 
      experiencing: true 
    }));
    
    addMessage({
      id: `typing-${Date.now()}`,
      timestamp: Date.now(),
      sender: 'emcog',
      content: '',
      type: MessageType.TYPING
    });
  }, [uploadAudioFile, addMessage]);

  // Handle image feedback
  const handleImageFeedback = useCallback((imageId: string, rating: number, feedback?: string) => {
    sendMessage({
      type: 'image_feedback',
      imageId,
      rating,
      feedback,
      timestamp: Date.now(),
      sessionId
    });
  }, [sendMessage, sessionId]);

  const handleSendTestMessage = useCallback(() => {
    sendMessage({
      type: 'chat',
      content: 'Test message for consciousness',
      timestamp: Date.now(),
      sessionId
    });
  }, [sendMessage, sessionId]);

  // Sync input mode with the useVoice hook: entering voice mode starts the
  // realtime session (WebSocket /voice, OpenAI Realtime API); leaving stops
  // it. Server-side VAD handles turn-taking once the session is live.
  const handleInputModeChange = useCallback((mode: 'text' | 'voice') => {
    console.log('🔄 App.tsx handleInputModeChange:', mode);
    setVoiceState(prev => ({
      ...prev,
      currentInputMode: mode
    }));
    hookSetInputMode(mode);

    if (mode === 'voice') {
      hookStartRecording();
    } else {
      hookStopRecording();
    }
  }, [hookSetInputMode, hookStartRecording, hookStopRecording]);

  return (
    <EmotionContext.Provider value={{ emotion, setEmotion }}>
      <Workspace
        emcogName={emcogName}
        userName={userName}
        connected={status === ConnectionStatus.CONNECTED}
        connecting={status === ConnectionStatus.CONNECTING}
        onReconnect={reconnect}
        thinking={!!(activeProcesses.thinking || capabilityStatus.status === 'starting')}
        mood={consciousnessState.isConscious ? consciousnessState.emotionalTone : undefined}
        dndEnabled={dndEnabled}
        onToggleDnd={toggleDnd}
        mind={consciousnessState}
        onLock={logout}
      >
        <div className="ws-stream" ref={streamRef}>
          <AdventureSessionBanner />
          {threadMore && (
            <div className="ws-earlier">
              <button type="button" className="ws-btn" onClick={showEarlier} disabled={threadLoading}>{threadLoading ? 'Loading…' : 'Show earlier'}</button>
            </div>
          )}
          <ChatContainer
            messages={messages}
            onImageFeedback={handleImageFeedback}
            consciousnessActive={consciousnessState.isConscious}
            emcogName={emcogName}
            userName={userName}
            capabilityStatus={capabilityStatus}
            capabilityHistory={capabilityHistory}
          />
        </div>
        <div className="input-area-container">
          {farFromBottom && (
            <button type="button" className="ws-jump-bottom" onClick={jumpToBottom} aria-label="Jump to the newest message" title="Jump to the newest message">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 5v14M5 12l7 7 7-7" /></svg>
            </button>
          )}
          <InputArea
            onSendMessage={sendMessageWithMode}
            onFileUpload={handleFileUpload}
            onFilesUpload={handleFilesUpload}
            onAudioFileUpload={handleAudioFileUpload}
            disabled={status !== ConnectionStatus.CONNECTED}
            voiceEnabled={voiceState.isVoiceEnabled}
            voiceSettings={voiceSettings}
            voiceState={voiceState}
            onInputModeChange={handleInputModeChange}
          />
        </div>
      </Workspace>
      {showDebug && (
        <WebSocketDebug
          status={status}
          lastMessage={lastMessage}
          sessionId={sessionId}
          onSendTest={handleSendTestMessage}
          consciousnessState={consciousnessState}
        />
      )}
    </EmotionContext.Provider>
  );
}

export default App;